import classNames from 'classnames';
import * as React from 'react';
import { Button, Form } from 'react-bootstrap';

import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import SelectItem from 'components/SelectItem';
import Spinner from 'components/Spinner';
import TagSelect from 'components/TagSelect';
import Tooltip from 'components/Tooltip';
import {
  DocumentClassification,
  DocumentClassificationMedcomms,
  isVeevaMedcomms,
  MAX_DOCUMENT_NAME_LENGTH,
} from 'const';
import { intlGet } from 'utils/intlGet';
import { SaveReusableLayoutProps } from './models';
import styles from './styles.module.scss';

const SaveLayout: React.FunctionComponent<SaveReusableLayoutProps> = ({
  initialClassification,
  documentClassifications,
  cancelPreviewGeneration,
  generatePreview,
  hideModal,
  initialLayoutName = '',
  layoutPreview,
  modalWindowId,
  saveLayout,
  updateAppState,
  title,
  audienceTypeData,
  channelTypeData,
  contentTypeData,
}) => {
  const defaultClassification = isVeevaMedcomms() ? DocumentClassificationMedcomms.HEADER : DocumentClassification.UNCLASSIFIED;
  const [classification, setClassification] = React.useState(initialClassification || defaultClassification);
  const [audienceType, setAudienceType] = React.useState<string[]>([]);
  const [channelType, setChannelType] = React.useState<string[]>([]);
  const [contentType, setContentType] = React.useState<string[]>([]);
  const [isSaveButtonTooltipShown, setIsSaveButtonTooltipShown] = React.useState(false);

  const saveButtonRef = React.createRef<HTMLDivElement>();

  React.useEffect(
    () => {
      generatePreview();

      return () => {
        // since we store temporary preview info in redux, have to clean it up and cancel running task
        // todo: replace with separate action
        updateAppState({ tempLayoutPreview: { isBeingGenerated: false, s3Url: null, preloadedUrl: null } });
        cancelPreviewGeneration();
      };
    },
    [],
  );

  const [layoutName, toggleLayoutName] = React.useState(initialLayoutName);

  const onCancelClick = (): void => {
    // HACK: pass the second argument to allow us to recognize which option has been chosen
    // TODO: modal should be opened from the saga and that saga will be waiting for response from modal
    hideModal(modalWindowId, false);
  };
  const onSaveClick: React.MouseEventHandler = (): void => {
    // HACK: pass the second argument to allow us to recognize which option has been chosen
    // TODO: modal should be opened from the saga and that saga will be waiting for response from modal
    hideModal(modalWindowId, true);
    // NOTE: it's nessacary to dispatch 'saveLayout' action after 'hideModal'
    // since 'resetLayoutAssets' saga should be run before 'saveLayout' saga
    saveLayout(layoutName, classification, audienceType, channelType, contentType);
  };

  const onNameChange: React.ChangeEventHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleLayoutName(event.target.value);
  };

  const renderLayoutPreview = (showTagsSection: boolean): JSX.Element => {
    const preloadedUrl = layoutPreview.get('preloadedUrl');

    return preloadedUrl
      ? <div
        className={showTagsSection ? classNames(styles.scrollableContainer, styles.withTags) : styles.scrollableContainer}>
        <img className={styles.image} src={preloadedUrl} />
      </div>
      : <Icon className={styles.placeholder} type={IconType.IMAGE} size="lg" color="disabled" />;
  };

  const changeDocumentClassification: React.ChangeEventHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setClassification(event.target.value as DocumentClassification);
  };

  const showSaveButtonTooltip = (): void => setIsSaveButtonTooltipShown(true);
  const hideSaveButtonTooltip = (): void => setIsSaveButtonTooltipShown(false);

  const hasEmptyTagsCollections = !audienceTypeData && !channelTypeData && !contentTypeData;
  const hasMissingTags = !audienceType.length || !channelType.length || !contentType.length;
  const isSaveButtonDisabled = !layoutName || (isVeevaMedcomms() && !hasEmptyTagsCollections && hasMissingTags);

  const showTagsSection = isVeevaMedcomms() && !hasEmptyTagsCollections;

  return (
    <div className={styles.SaveLayout}>
      <header className={styles.header}>
        <div>{title}</div>
        <Icon className={styles.closeIcon} onClick={onCancelClick} type={IconType.CLOSE} size="sm-md" color="primary" />
      </header>
      <main className={styles.main}>
        <section className={styles.preview}>
          {
            layoutPreview.get('isBeingGenerated')
              ? <Spinner className={styles.spinner} />
              : renderLayoutPreview(showTagsSection)
          }
        </section>

        <section className={styles.formSection}>
          <div className={styles.formSectionName}>
            <h3 className={styles.selectTitle}>{intlGet('SaveReusableLayout.Form', 'NameLabel')}</h3>
            <Form.Control
              className={styles.name}
              type="text"
              placeholder={intlGet('SaveReusableLayout.Form', 'Name')}
              maxLength={MAX_DOCUMENT_NAME_LENGTH}
              value={layoutName}
              onChange={onNameChange}
            />
          </div>
          <div className={styles.formSectionClassification}>
            {documentClassifications &&
          <>
            <h3 className={styles.selectTitle}>{intlGet('SaveReusableLayout.Select', 'Classification')}</h3>
            <SelectItem
              className={styles.selectWrapper}
              onChange={changeDocumentClassification}
              value={classification}
              values={documentClassifications}
            />
          </>
            }
          </div>
        </section>
        {showTagsSection && <section className={styles.formSectionTags}>
          <div className={styles.formSectionTagsRow}>
            <h4 className={styles.formSectionTagsRowHeaderName}>{intlGet('SaveReusableLayout.Form.Tags.Header', 'Name')}</h4>
            <h4 className={styles.formSectionTagsRowHeaderValues}>{intlGet('SaveReusableLayout.Form.Tags.Header', 'Values')}</h4>
          </div>
          <div className={styles.formSectionTagsRow}>
            <div className={styles.formSectionTagsRowTagName}>
              <span>{intlGet('SaveReusableLayout.Form.Tags', 'AudienceType')}</span>
            </div>
            <div className={styles.formSectionTagsRowTagValue}>
              <TagSelect
                options={audienceTypeData?.entryLabels ?? []}
                value={audienceType}
                onChange={setAudienceType}
                singleSelect={!audienceTypeData?.repeating}
              />
            </div>
          </div>
          <div className={styles.formSectionTagsRow}>
            <div className={styles.formSectionTagsRowTagName}>
              <span>{intlGet('SaveReusableLayout.Form.Tags', 'ChannelType')}</span>
            </div>
            <div className={styles.formSectionTagsRowTagValue}>
              <TagSelect
                options={channelTypeData?.entryLabels ?? []}
                value={channelType}
                onChange={setChannelType}
                singleSelect={!channelTypeData?.repeating}
              />
            </div>
          </div>
          <div className={styles.formSectionTagsRow}>
            <div className={styles.formSectionTagsRowTagName}>
              <span>{intlGet('SaveReusableLayout.Form.Tags', 'ContentType')}</span>
            </div>
            <div className={styles.formSectionTagsRowTagValue}>
              <TagSelect
                options={contentTypeData?.entryLabels ?? []}
                value={contentType}
                onChange={setContentType}
                singleSelect={!contentTypeData?.repeating}
              />
            </div>
          </div>
        </section>}
      </main>
      <footer className={styles.footer}>
        <Button onClick={onCancelClick} variant="secondary">{intlGet('SaveReusableLayout.Buttons', 'Cancel')}</Button>
        <div
          ref={saveButtonRef}
          onMouseEnter={showSaveButtonTooltip}
          onMouseLeave={hideSaveButtonTooltip}>
          <Button
            onClick={onSaveClick}
            variant="primary"
            disabled={isSaveButtonDisabled}
          >
            {intlGet('SaveReusableLayout.Buttons', 'Save')}
          </Button>
        </div>
        {showTagsSection && <Tooltip
          className={styles.tooltip}
          parent={saveButtonRef}
          show={hasMissingTags && isSaveButtonTooltipShown}
          text={intlGet('SaveReusableLayout.Buttons.Save', 'Tooltip')}/>}
      </footer>
    </div>
  );
};

export default SaveLayout;
